import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type CollectionResponse, type ResourceResponse } from '@shared/api/client';
import type { FormaPago, FormaPagoFormData } from '../types/forma-pago';

const BASE = '/billing/payment-methods';

export const formaPagoKeys = {
  all: ['formas-pago'] as const,
  list: (clienteId: number) => [...formaPagoKeys.all, 'list', clienteId] as const,
};

export function useFormasPago(clienteId: number | undefined) {
  return useQuery({
    queryKey: formaPagoKeys.list(clienteId ?? 0),
    queryFn: async () => {
      const { data } = await api.get<CollectionResponse<FormaPago>>(BASE, {
        params: { cliente_id: clienteId },
      });
      return data.data;
    },
    enabled: !!clienteId,
  });
}

export function useCreateFormaPago() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: FormaPagoFormData) => {
      const { data } = await api.post<ResourceResponse<FormaPago>>(BASE, payload);
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: formaPagoKeys.all }),
  });
}

export function useUpdateFormaPago() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, payload }: { id: number; payload: Partial<FormaPagoFormData> }) => {
      const { data } = await api.patch<ResourceResponse<FormaPago>>(`${BASE}/${id}`, payload);
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: formaPagoKeys.all }),
  });
}

export function useDeleteFormaPago() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => api.delete(`${BASE}/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: formaPagoKeys.all }),
  });
}
